import React from 'react'
import Image from 'next/future/image'
import F1Heading from '@shared/components/F1Components/F1Heading'
import PropTypes from 'prop-types'
import rank1 from '@assets/images/f1/img/rank1.png'
import rank2 from '@assets/images/f1/img/rank2.png'
import rank3 from '@assets/images/f1/img/rank3.png'
import evenBg from '@assets/images/f1/icons/evenBg.png'
import oddBg from '@assets/images/f1/icons/oddBg.png'

import styles from './style.module.scss'
import BreadcrumbNav from '@shared/components/breadcrumbNav'
import Formula1Layout from '../layout'
import { handleApiError } from '@shared-submodule/utils'
import CustomLink from '@shared/components/customLink'
import queryGraphql from '@shared/components/queryGraphql'
import Layout from '@shared/components/layout'
import { GRAPHQL_URL } from '@shared-submodule/sm-constants'

const rankImages = {
  1: { src: rank1.src, color: '#2F4794' },
  2: { src: rank2.src, color: '#CB1E2A' },
  3: { src: rank3.src, color: 'black' }
}

const TopDriverCard = ({ driver }) => {
  const { src, color } = rankImages[driver?.nPosition] || rankImages[1]

  return (
    <CustomLink href={`/${driver?.oSeo?.sSlug}`}>
      <div className={styles.layout} style={{ backgroundImage: `url(${src})`, backgroundSize: 'cover' }}>
        <div className={styles.data}>
          <div className="d-flex flex-column gap-3">
            <span className={styles.fname} style={{ color }}>
              {driver?.oDriver?.sName}
            </span>
            <span className={styles.lname} style={{ color }}>
              {driver?.oDriver?.sFamilyName}
            </span>
            <div className={styles.line} style={{ backgroundColor: color }}></div>
            <span className={styles.brand} style={{ color }}>
              {driver?.aTeams?.[0]?.sName}
            </span>
          </div>
          <span className={styles.points}>{driver?.nPoints}</span>
        </div>
        <div className={styles.driverImg}>
          <Image src={driver?.oDriver?.sCountryFlag} alt="Country" width={70} height={54} layout="raw" />
          <Image
            src={driver?.oDriver?.sImgUrl}
            alt={`${driver?.oDriver?.sName} ${driver?.oDriver?.sFamilyName}`}
            width={280}
            height={280}
          />
        </div>
      </div>
    </CustomLink>
  )
}

TopDriverCard.propTypes = {
  driver: PropTypes.array
}

const DriverCard = ({ driver, index }) => {
  const bgImage = index % 2 === 0 ? evenBg.src : oddBg.src

  return (
    <CustomLink href={`/${driver?.oSeo?.sSlug}`}>
      <div className={styles.layout} style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover' }}>
        <div className={styles.data}>
          <span className={styles.rank}>#{driver?.nPosition}</span>
          <span className={styles.fname}>{driver?.oDriver?.sName}</span>
          <div className="d-flex flex-row align-items-center gap-3">
            <span className={styles.lname}>{driver?.oDriver?.sFamilyName}</span>
            <Image src={driver?.oDriver?.sCountryFlag} alt="Country" width={55} height={35} layout="raw" style={{ borderRadius: '10px' }} />
          </div>
          <div className={styles.line}></div>
          <span className={styles.brand}>{driver?.aTeams?.[0]?.sName}</span>
          <span className={styles.points}>{driver?.nPoints}</span>
        </div>
        <div className={styles.driverImg}>
          <Image
            src={driver?.oDriver?.sImgUrl}
            alt={`${driver?.oDriver?.sName} ${driver?.oDriver?.sFamilyName}`}
            style={{ objectFit: 'cover' }}
            width={258}
            height={240}
          />
        </div>
      </div>
    </CustomLink>
  )
}

DriverCard.propTypes = {
  driver: PropTypes.array,
  index: PropTypes.number
}

const Drivers = ({ driversData, seoData }) => (
  <Layout data={{ oSeo: seoData }}>
    <Formula1Layout>
      <div className={styles.driversLayout}>
        <div className="flex flex-column">
          <BreadcrumbNav />
          <F1Heading title="F1 Drivers 2025" />
          <p className={styles.para}>
            Check out this season’s official F1 line-up, full breakdown of drivers, points and current positions.
          </p>
        </div>

        <div className={styles.driversList}>
          <div className={styles.ranker}>
            {(driversData?.data ?? []).slice(0, 3).map(driver => (
              <TopDriverCard key={driver.nPosition} driver={driver} />
            ))}
          </div>

          <div className={styles.list}>
            {(driversData?.data ?? []).slice(3).map((driver, i) => (
              <DriverCard key={driver.nPosition} driver={driver} index={i} />
            ))}
          </div>
        </div>
      </div>
    </Formula1Layout>
  </Layout>
)

Drivers.propTypes = {
  seoData: PropTypes.object,
  driversData: PropTypes.object
}

export default Drivers

export async function getServerSideProps({ res, resolvedUrl }) {
  try {
    res.setHeader('Cache-Control', 'public, max-age=600')

    const GET_SEO_DATA = (await import('@graphql/article/article.query')).GET_SEO_DATA
    const { data: seo } = await queryGraphql(GET_SEO_DATA, { input: { sSlug: 'formula1-racing/drivers' } })

    const BASE_URL = GRAPHQL_URL.replace(/\/graphql$/, '')

    const urlDrivers = new URL(`${BASE_URL}/api/global-widget/f1/drivers`)
    urlDrivers.searchParams.append('nLimit', '50')

    const driversResponse = await fetch(urlDrivers, {
      method: 'GET',
      headers: {
        'x-api-key': process.env.REACT_APP_API_KEY,
        'Content-Type': 'application/json'
      }
    })
    const driversData = await driversResponse.json()

    return {
      props: {
        seoData: seo?.getSeoData || {},
        driversData: driversData || {}
      }
    }
  } catch (e) {
    console.error('Error fetching data:', e)
    res.setHeader('Cache-Control', 'no-cache')
    const status = handleApiError(e, resolvedUrl)
    return status
  }
}
